// frontend/src/components/Home.jsx
import { useGameData } from "../hooks/useGameData";
import GameSlider from "./GameSlider";
import { useGameModal } from "../context/GameModalContext";

// SEO
import useDocumentMeta from "../hooks/useDocumentMeta";
import useJsonLd from "../hooks/useJsonLd";

// Iconos (lucide)
import { Trophy, Star, Calendar as CalendarIcon } from "lucide-react";
// Logo Steam
import steamLogo from "../assets/icons/steam-logo.png";

// ...otros imports
import XPHubLetsYou from "./XPHubLetsYou";
import SteamNewsFeed from "./SteamNewsFeed";
import LandingPage from "./LandingPage"; // Marketing page para no auth — para revertir: quitar import y early-return

function Home({ isAuthenticated }) {
  // ── SEO ───────────────────────────────────────────────────────────────────
  useDocumentMeta({
    title: "XPHub — Tu historia gamer",
    description:
      "Construye tu historia gamer: reúne tu biblioteca, registra sesiones, comparte reviews y conecta Steam en XPHub.",
    canonical: "https://xphub.gg/",
    robots: "index,follow",
    og: {
      title: "XPHub — Tu historia gamer",
      description:
        "Steam guarda tus horas. XPHub construye tu historia gamer.",
      url: "https://xphub.gg/",
      type: "website",
    },
    twitter: {
      card: "summary",
      title: "XPHub — Tu historia gamer",
      description:
        "Steam guarda tus horas. XPHub construye tu historia gamer.",
    },
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "XPHub",
      url: "https://xphub.gg/",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://xphub.gg/search?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "XPHub",
      url: "https://xphub.gg/",
      logo: "https://xphub.gg/images/xphub-logo.png",
    },
  ]);
  // ─────────────────────────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return <AuthenticatedHome />;
}

function AuthenticatedHome() {

  const ICON_COLOR = "#fcb14a";

  const selectedPlatform = "";
  const { topRatedGames, latestGames, steamGames, bestOfYear, loading, errors, refresh, lastUpdatedAt } =
    useGameData(selectedPlatform);
  const { openModal } = useGameModal();
  const hasFeedWarning = Boolean(errors.catalog || errors.steam);
  const lastSyncLabel = lastUpdatedAt
    ? new Date(lastUpdatedAt).toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div className="container home-container">
      {hasFeedWarning && (
        <div
          style={{
            margin: "0 0 16px",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(252,177,74,0.45)",
            background: "rgba(252,177,74,0.08)",
            color: "#ffd79a",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            {errors.catalog || errors.steam}
            {lastSyncLabel && (
              <div style={{ marginTop: 4, color: "#c7d3e3", fontSize: 12 }}>
                Última sincronización correcta: {lastSyncLabel}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={refresh}
            style={{
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.28)",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              fontWeight: 700,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* SLIDERS */}
      <GameSlider
        title="Lo Mejor del Año"
        games={bestOfYear}
        loading={loading.bestOfYear}
        imageOnly
        onClick={(game) =>
          openModal({
            ...game,
            source: "igdb",
            context: { collection: "best-of-year" },
          })
        }
        viewAllTo="/browse/best-of-year"
        leftIcon={<Trophy size={26} color={ICON_COLOR} strokeWidth={2.2} />}
      />

      <XPHubLetsYou />

      <GameSlider
        title="Juegos mejor valorados"
        games={topRatedGames}
        loading={loading.topRated}
        imageOnly
        onClick={(game) =>
          openModal({
            ...game,
            source: "igdb",
            context: { collection: "top-rated" },
          })
        }
        viewAllTo="/browse/top-rated"
        leftIcon={<Star size={26} color={ICON_COLOR} strokeWidth={2.2} />}
      />

      <GameSlider
        title="Próximos lanzamientos"
        games={latestGames}
        loading={loading.latest}
        imageOnly
        onClick={(game) =>
          openModal({
            ...game,
            source: "igdb",
            context: { collection: "upcoming" },
          })
        }
        viewAllTo="/browse/upcoming"
        isLatest
        leftIcon={<CalendarIcon size={26} color={ICON_COLOR} strokeWidth={2.2} />}
      />

      <GameSlider
        title="Los más jugados en Steam"
        games={steamGames}
        loading={loading.steam}
        imageOnly
        onClick={(game) =>
          openModal({
            ...game,
            source: "steam",
            context: { collection: "steam-trending" },
          })
        }
        viewAllTo="/browse/steam-trending"
        showPlayers
        leftIcon={
          <img
            src={steamLogo}
            alt="Steam"
            style={{ width: 26, height: 26, objectFit: "contain", display: "block" }}
          />
        }
      />

      {/* ⬇️ NUEVO: Noticias Steam (al final de Home) */}
      <SteamNewsFeed
        steamGames={steamGames}
        limit={9}
        titleLinkTo="/news"
      />
    </div>
  );
}

export default Home;
